import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import "../../styles/EnvironmentalPolicy.css";
import EvidenceReferenceField from "../../components/EvidenceReferenceField";
import {
  approveEnvironmentalPolicyApi,
  canApproveEmsModule,
  fetchEnvironmentalPolicyApi,
  updateEnvironmentalPolicyApi,
} from "../../lib/api";

const EMPTY_FORM = {
  title: "Environmental Policy",
  policy_statement: "",
  scope: "",
  commitments: "",
  review_date: "",
  version: "1.0",
  evidence: "",
  notes: "",
};

export default function EnvironmentalPolicy() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const canApprove = canApproveEmsModule("environmental_policy");

  const loadRecord = async () => {
    try {
      setLoading(true);
      const data = await fetchEnvironmentalPolicyApi();
      setRecord(data || null);
      setForm({
        title: data?.title || "Environmental Policy",
        policy_statement: data?.policy_statement || "",
        scope: data?.scope || "",
        commitments: data?.commitments || "",
        review_date: data?.review_date || "",
        version: data?.version || "1.0",
        evidence: data?.evidence || "",
        notes: data?.notes || "",
      });
    } catch (err) {
      toast.error(err?.message || "Failed to load environmental policy");
      setRecord(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecord();
  }, []);

  const summary = useMemo(() => ([
    { label: "Status", value: String(record?.status || "draft") },
    { label: "Version", value: String(record?.version || "1.0") },
    { label: "Approved by", value: String(record?.approved_by || "-") || "-" },
    { label: "Review date", value: String(record?.review_date || "-") || "-" },
  ]), [record]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!String(form.policy_statement || "").trim()) {
      toast.error("Policy statement is required");
      return;
    }
    try {
      setSaving(true);
      const updated = await updateEnvironmentalPolicyApi(form);
      setRecord(updated || null);
      toast.success("Environmental policy saved");
    } catch (err) {
      toast.error(err?.message || "Failed to save environmental policy");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    try {
      setApproving(true);
      const notes = window.prompt("Optional approval note:", "") || "";
      const resp = await approveEnvironmentalPolicyApi({ notes });
      setRecord(resp?.policy || null);
      toast.success("Environmental policy approved");
    } catch (err) {
      toast.error(err?.message || "Failed to approve environmental policy");
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="dashboard-page environmental-policy-page">
      <div className="dashboard-header">
        <div>
          <p className="environmental-policy-eyebrow">ISO 14001 policy setup</p>
          <h1>Environmental Policy</h1>
          <p className="subtitle">Create the policy your organisation will follow, communicate and review. Complete the organisation-specific details below, then submit it for approval.</p>
        </div>
      </div>

      <div className="summary-row">
        {summary.map((item) => (
          <div className="kpi-card" key={item.label}>
            <div className="kpi-label">{item.label}</div>
            <h2>{loading ? "-" : item.value}</h2>
          </div>
        ))}
      </div>

      <section className="card environmental-policy-guidance">
        <div>
          <p className="environmental-policy-eyebrow">Before you start</p>
          <h3>Write a policy that reflects how your organisation operates</h3>
          <p>Use plain language, name the activities and locations covered, and make commitments your organisation can demonstrate in practice.</p>
        </div>
        <div className="environmental-policy-guidance-grid">
          <div><strong>1. Define coverage</strong><span>State the sites, teams and activities included in the policy.</span></div>
          <div><strong>2. Set commitments</strong><span>Confirm the environmental commitments leadership will support.</span></div>
          <div><strong>3. Keep evidence</strong><span>Link the approved policy or supporting management approval.</span></div>
        </div>
      </section>

      <div className="card settings-card environmental-policy-form">
        <div className="settings-header">
          <div>
            <p className="environmental-policy-eyebrow">Your policy</p>
            <h3>Complete the policy record</h3>
          </div>
          <span className="kpi-sub">Edits reset approval until an authorised approver signs off again.</span>
        </div>
        <form onSubmit={handleSave}>
          <div className="settings-grid">
            <label className="settings-field">
              <span>Title</span>
              <input placeholder="e.g. Hindsight Sustainability Environmental Policy" value={form.title} onChange={(e) => setField("title", e.target.value)} disabled={saving || loading} />
              <small>Use a clear document title that identifies your organisation.</small>
            </label>
            <label className="settings-field">
              <span>Version</span>
              <input placeholder="e.g. 1.0" value={form.version} onChange={(e) => setField("version", e.target.value)} disabled={saving || loading} />
              <small>Start at 1.0. Increase the version when an approved policy is materially changed.</small>
            </label>
            <label className="settings-field">
              <span>Review date</span>
              <input type="date" value={form.review_date} onChange={(e) => setField("review_date", e.target.value)} disabled={saving || loading} />
              <small>Set the next planned review date, normally within 12 months or sooner after a significant change.</small>
            </label>
            <EvidenceReferenceField
              label="Evidence link or attachment reference"
              value={form.evidence}
              onChange={(value) => setField("evidence", value)}
              disabled={saving || loading}
              helpText="Link the signed policy, management approval, meeting minutes, or another record that supports this policy."
            />
            <label className="settings-field environmental-policy-wide-field">
              <span>Scope</span>
              <small>State who and what this policy covers: legal entity, sites, employees, contractors and key activities.</small>
              <textarea rows={3} value={form.scope} onChange={(e) => setField("scope", e.target.value)} disabled={saving || loading} placeholder="e.g. This policy applies to Hindsight Sustainability's UK office, warehouse, employees and contractors involved in distribution operations." />
            </label>
            <label className="settings-field environmental-policy-wide-field">
              <span>Policy statement</span>
              <small>Write a short leadership statement explaining why environmental management matters to your organisation and how the policy will be communicated and maintained.</small>
              <textarea rows={6} value={form.policy_statement} onChange={(e) => setField("policy_statement", e.target.value)} disabled={saving || loading} placeholder="e.g. Hindsight Sustainability is committed to managing the environmental impacts of its operations and improving environmental performance through clear objectives, controls and regular review." />
            </label>
            <label className="settings-field environmental-policy-wide-field">
              <span>Commitments</span>
              <small>List the commitments leadership will make and evidence, such as compliance, pollution prevention, resource efficiency, carbon reduction and continual improvement.</small>
              <textarea rows={4} value={form.commitments} onChange={(e) => setField("commitments", e.target.value)} disabled={saving || loading} placeholder="e.g. We will meet applicable compliance obligations, prevent pollution, reduce waste and energy use, and continually improve our environmental management system." />
            </label>
            <label className="settings-field environmental-policy-wide-field">
              <span>Notes</span>
              <small>Optional: record the policy owner, communication plan, consultation carried out, or follow-up actions before approval.</small>
              <textarea rows={3} value={form.notes} onChange={(e) => setField("notes", e.target.value)} disabled={saving || loading} placeholder="e.g. Policy owner: Operations Director. Display in staff area and issue to relevant contractors." />
            </label>
          </div>
          <div className="settings-actions">
            <button className="btn-primary" type="submit" disabled={saving || loading}>
              {saving ? "Saving..." : "Save policy"}
            </button>
            {canApprove ? (
              <button
                className="btn-secondary"
                type="button"
                disabled={approving || loading || !String(form.policy_statement || "").trim()}
                onClick={handleApprove}
              >
                {approving ? "Approving..." : "Approve policy"}
              </button>
            ) : null}
          </div>
        </form>
      </div>
    </div>
  );
}
